import { useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useRealtime } from '../../context/RealtimeContext'
import { useToast } from '../../context/ToastContext'
import { Mail, Shield, Clock, TrendingUp, AlertTriangle, CheckSquare, KeyRound, Eye, EyeOff } from 'lucide-react'
import { supabase } from '../../lib/supabase'

export default function EmployeeProfile() {
  const { currentUser } = useAuth()
  const { tasksFor }    = useRealtime()
  const { toast }       = useToast()

  const [pwForm, setPwForm]   = useState({ current: '', newPw: '', confirm: '' })
  const [pwError, setPwError] = useState('')
  const [pwLoading, setPwLoading] = useState(false)
  const [showPw, setShowPw]   = useState(false)

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setPwError('')
    if (!pwForm.newPw)                          { setPwError('Enter a new password.'); return }
    if (pwForm.newPw.length < 8)               { setPwError('Password must be at least 8 characters.'); return }
    if (pwForm.newPw !== pwForm.confirm)        { setPwError('Passwords do not match.'); return }

    setPwLoading(true)
    const { error } = await supabase.auth.updateUser({ password: pwForm.newPw })
    setPwLoading(false)

    if (error) { setPwError(error.message); return }
    toast('Password updated successfully!')
    setPwForm({ current: '', newPw: '', confirm: '' })
  }

  const iStyle: React.CSSProperties = {
    width: '100%', background: '#1a1a1a',
    border: '1px solid rgba(255,198,0,0.2)',
    borderRadius: 8, padding: '9px 12px', color: '#fff',
    fontSize: 13, outline: 'none',
  }
  const myTasks         = tasksFor(currentUser?.name ?? '')

  const done        = myTasks.filter(t => t.status === 'done')
  const active      = myTasks.filter(t => t.status === 'in-progress')
  const overdue     = myTasks.filter(t => t.status !== 'done' && new Date(t.dueDate) < new Date())
  const completion  = myTasks.length > 0 ? Math.round((done.length / myTasks.length) * 100) : 0

  const stats = [
    { label: 'Total Tasks',  value: myTasks.length,  icon: CheckSquare,   color: '#ffc600' },
    { label: 'Completed',    value: done.length,      icon: TrendingUp,    color: '#e8a000' },
    { label: 'In Progress',  value: active.length,    icon: Clock,         color: '#ff8c00' },
    { label: 'Overdue',      value: overdue.length,   icon: AlertTriangle, color: overdue.length > 0 ? '#cc3300' : '#555' },
  ]

  function roleLabel(founderKey?: string) {
    if (!founderKey) return 'Employee'
    const map: Record<string, string> = { bunny: 'Production Team', hj: 'Sales & Ops Team', darshu: 'Paid Ads Team' }
    return map[founderKey] ?? 'Employee'
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>My Profile</h1>
        <p className="text-sm mt-0.5" style={{ color: '#a0a0a0' }}>Your account details and performance overview</p>
      </div>

      {/* Profile card */}
      <div className="rounded-xl p-6" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)' }}>
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-xl font-bold flex-shrink-0"
            style={{ background: '#ffc600', color: '#000', fontFamily: 'Syne, sans-serif' }}>
            {currentUser?.avatarInitials}
          </div>
          <div>
            <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>{currentUser?.name}</h2>
            <p className="text-sm" style={{ color: '#a0a0a0' }}>{roleLabel(currentUser?.founderKey)}</p>
            <div className="flex items-center gap-1.5 mt-1">
              <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#e8a000' }} />
              <span className="text-xs" style={{ color: '#555' }}>Active</span>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg" style={{ background: '#1a1a1a' }}>
            <Mail size={15} style={{ color: '#ffc600' }} />
            <div>
              <p className="text-xs" style={{ color: '#555' }}>EMAIL</p>
              <p className="text-sm text-white">{currentUser?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg" style={{ background: '#1a1a1a' }}>
            <Shield size={15} style={{ color: '#ffc600' }} />
            <div>
              <p className="text-xs" style={{ color: '#555' }}>ROLE</p>
              <p className="text-sm text-white capitalize">{currentUser?.role}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg" style={{ background: '#1a1a1a' }}>
            <Clock size={15} style={{ color: '#ffc600' }} />
            <div>
              <p className="text-xs" style={{ color: '#555' }}>MEMBER SINCE</p>
              <p className="text-sm text-white">
                {currentUser?.createdAt
                  ? new Date(currentUser.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
                  : '—'}
              </p>
            </div>
          </div>
          {currentUser?.lastLogin && (
            <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg" style={{ background: '#1a1a1a' }}>
              <Shield size={15} style={{ color: '#555' }} />
              <div>
                <p className="text-xs" style={{ color: '#555' }}>LAST LOGIN</p>
                <p className="text-sm text-white">
                  {new Date(currentUser.lastLogin).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Performance stats */}
      <div className="rounded-xl p-5" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)' }}>
        <h2 className="text-sm font-bold text-white mb-4">Performance Overview</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          {stats.map(({ label, value, icon: Icon, color }) => (
            <div key={label} className="rounded-lg p-3" style={{ background: '#1a1a1a' }}>
              <Icon size={13} style={{ color }} className="mb-1.5" />
              <p className="text-xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>{value}</p>
              <p className="text-xs mt-0.5" style={{ color: '#555' }}>{label}</p>
            </div>
          ))}
        </div>

        {/* Completion bar */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-xs font-semibold" style={{ color: '#a0a0a0' }}>COMPLETION RATE</p>
            <p className="text-xs font-bold text-white">{completion}%</p>
          </div>
          <div className="rounded-full overflow-hidden" style={{ height: 8, background: '#1a1a1a' }}>
            <div className="h-full rounded-full transition-all"
              style={{
                width: `${completion}%`,
                background: completion >= 75 ? '#e8a000' : completion >= 40 ? '#ffc600' : '#cc3300',
              }} />
          </div>
          <p className="text-xs mt-1" style={{ color: '#333' }}>{done.length} completed out of {myTasks.length} assigned</p>
        </div>
      </div>

      {/* Change Password */}
      <div className="rounded-xl p-5" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)' }}>
        <div className="flex items-center gap-2 mb-4">
          <KeyRound size={15} style={{ color: '#ffc600' }} />
          <h2 className="text-sm font-bold text-white">Change Password</h2>
        </div>
        <form onSubmit={handleChangePassword} className="space-y-3">
          <div className="relative">
            <label className="text-xs font-semibold block mb-1" style={{ color: '#a0a0a0' }}>NEW PASSWORD</label>
            <input type={showPw ? 'text' : 'password'} value={pwForm.newPw}
              onChange={e => setPwForm(f => ({ ...f, newPw: e.target.value }))}
              style={{ ...iStyle, paddingRight: 36 }} placeholder="Minimum 8 characters" />
            <button type="button" onClick={() => setShowPw(v => !v)}
              className="absolute right-3" style={{ top: '66%', transform: 'translateY(-50%)', color: '#555' }}>
              {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
          <div>
            <label className="text-xs font-semibold block mb-1" style={{ color: '#a0a0a0' }}>CONFIRM PASSWORD</label>
            <input type="password" value={pwForm.confirm}
              onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))}
              style={iStyle} placeholder="Re-enter new password" />
          </div>
          {pwError && <p className="text-xs" style={{ color: '#cc3300' }}>{pwError}</p>}
          <button type="submit" disabled={pwLoading}
            className="w-full py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2"
            style={{ background: pwLoading ? 'rgba(255,198,0,0.5)' : '#ffc600', color: '#000', cursor: pwLoading ? 'not-allowed' : 'pointer' }}>
            {pwLoading && <span className="w-3.5 h-3.5 border-2 border-black/30 border-t-black rounded-full animate-spin" />}
            Update Password
          </button>
        </form>
      </div>

      {/* Overdue tasks warning */}
      {overdue.length > 0 && (
        <div className="rounded-xl p-4" style={{ background: 'rgba(204,51,0,0.06)', border: '1px solid rgba(204,51,0,0.25)' }}>
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={14} style={{ color: '#cc3300' }} />
            <p className="text-sm font-semibold" style={{ color: '#cc3300' }}>
              {overdue.length} overdue task{overdue.length > 1 ? 's' : ''} need attention
            </p>
          </div>
          <div className="space-y-1.5">
            {overdue.map(t => (
              <div key={t.id} className="flex items-center justify-between">
                <p className="text-xs text-white truncate flex-1">{t.title}</p>
                <p className="text-xs ml-3 flex-shrink-0" style={{ color: '#cc3300' }}>
                  Due {new Date(t.dueDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
